import { useEffect, useState } from 'react';
// local
import { useNotification } from '../contexts/NotificationContext';
import { useUser } from '../contexts/UserContext';

export type Method =
  | 'get'
  | 'GET'
  | 'delete'
  | 'DELETE'
  | 'options'
  | 'OPTIONS'
  | 'post'
  | 'POST'
  | 'put'
  | 'PUT'
  | 'patch'
  | 'PATCH';

export type FetchError = {
  data: any;
  status: number;
  statusText: string;
};

export type ResponseType =
  | 'arraybuffer'
  | 'stream'
  | 'document'
  | 'json'
  | 'text';

export const useFetch = (disableNotifications?: boolean) => {
  const [error, setError] = useState<FetchError | null>(null);
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<Method | null>(null);
  const [response, setResponse] = useState<any>(null);
  const [controller, setController] = useState<AbortController>(
    new AbortController()
  );
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const notification = useNotification();
  const user = useUser();

  useEffect(() => {
    return () => {
      controller.abort();
      resetState();
    };
  }, []);

  const makeRequest = (
    url: string,
    method: Method,
    body?: any,
    responseType?: ResponseType,
    cache?: RequestCache
  ) => {
    if (loading) abort();

    const requestController = new AbortController();
    setController(requestController);

    resetState();

    setLoading(true);
    setMethod(method);

    fetch(url, {
      cache,
      headers: {
        Authorization: user.token ?? '',
        'Content-Type': 'application/json',
      },
      signal: requestController.signal,
      body: body ? JSON.stringify(body) : undefined,
      method,
    })
      .then(async (res) => {
        setIsSuccess(res.ok);
        if (res.ok) {
          let response = null;
          if (res.status !== 204) {
            switch (responseType) {
              case 'arraybuffer':
                response = await res.arrayBuffer();
                break;
              case 'document':
                response = await res.blob();
                break;
              case 'text':
                response = await res.text();
                break;
              case 'json':
              default:
                response = await res
                  .clone()
                  .json()
                  .catch(() => res.text());
            }
          }

          setResponse(response);
        } else {
          if (res.status === 401 || res.status === 403) {
            user.logout();
          }

          const error = await res.text();

          setError({
            data: error,
            status: res.status,
            statusText: res.statusText,
          });

          if (!disableNotifications && res.status !== 404) {
            notification.addNotification({
              dismissible: true,
              message: error ? error : 'Unknown error has occurred',
              name: error ? `Status Code: ${res.status}` : undefined,
              type: 'error',
            });
          } else if (!disableNotifications && res.status === 404) {
            notification.addNotification({
              dismissible: true,
              message: 'Not Found',
              name: error ? `Status Code: ${res.status}` : undefined,
              type: 'error',
            });
          }
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        const message =
          err instanceof Error ? err.message : 'Unable to complete the request';
        setIsSuccess(false);
        setError({ data: message, status: 0, statusText: 'Network error' });
        if (!disableNotifications) {
          notification.addNotification({
            dismissible: true,
            message,
            type: 'error',
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const resetState = () => {
    setError(null);
    setMethod(null);
    setResponse(null);
    setIsSuccess(null);
  };

  const abort = () => {
    controller.abort();
  };

  return {
    error,
    loading,
    makeRequest,
    method,
    response,
    resetState,
    isSuccess: isSuccess !== null && isSuccess,
  };
};
